import type { CustomerCategoryKey } from '@mds/shared/types/customer';

import ChipPicker from '@/components/ui/chip-picker';
import Field, { inputCls, selectCls } from '@/components/ui/field';
import Select from '@mds/shared/ui/select';
import { INTEREST_POOL, TAG_POOL } from '@/data/customers';
import { CHANNELS, CUSTOMER_CATEGORIES, type CustomerDraft } from '@/domain/customers';
import type { FieldBinder } from '@/lib/validation';

/**
 * A customer record's fields. The Customers page adds and edits with it, and
 * the booking form uses it to add someone new, so a customer created at the
 * desk is the same record as one created in the CRM.
 */
export default function CustomerForm({
  draft,
  onChange,
  editing = false,
  field,
}: {
  draft: CustomerDraft;
  onChange: (next: CustomerDraft) => void;
  /** An existing record, where clearing the email removes the address. */
  editing?: boolean;
  /** The parent form's validation, checked against `customerSchema`. */
  field: FieldBinder;
}) {
  const set = (patch: Partial<CustomerDraft>) => onChange({ ...draft, ...patch });

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" required {...field('name')}>
          <input
            className={inputCls}
            value={draft.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>
        <Field
          label="Category"
          required
          hint="Selecting another category replaces the current one."
          {...field('categoryKey')}
        >
          <Select
            variant="field"
            className={selectCls}
            value={draft.categoryKey}
            onChange={(k) => set({ categoryKey: k as CustomerCategoryKey })}
            options={CUSTOMER_CATEGORIES.map((c) => ({ value: c.key, label: c.name }))}
            aria-label="Customer category"
          />
        </Field>
        <Field
          label="Email"
          hint={editing ? 'Optional. Clearing it removes the address.' : 'Optional.'}
          {...field('email')}
        >
          <input
            type="email"
            className={inputCls}
            value={draft.email}
            onChange={(e) => set({ email: e.target.value })}
          />
        </Field>
        <Field label="Phone" hint="Optional." {...field('phone')}>
          <input
            type="tel"
            className={inputCls}
            value={draft.phone}
            onChange={(e) => set({ phone: e.target.value })}
          />
        </Field>
      </div>

      <ChipPicker
        label="Tags"
        pool={TAG_POOL}
        value={draft.tags}
        onChange={(tags) => set({ tags })}
        placeholder="Add a tag…"
        {...field('tags', { within: true })}
      />

      <ChipPicker
        label="Interests"
        pool={INTEREST_POOL}
        value={draft.interests}
        onChange={(interests) => set({ interests })}
        placeholder="Add an interest…"
        {...field('interests', { within: true })}
      />

      <fieldset className="m-0 border-0 p-0">
        <legend className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
          Happy to be contacted on
        </legend>
        <div className="mt-3 flex flex-wrap gap-4">
          {CHANNELS.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2.5 text-base">
              <input
                type="checkbox"
                className="h-6 w-6 accent-brand-edge"
                checked={draft.communicationPreferences[key]}
                onChange={(e) =>
                  set({
                    communicationPreferences: {
                      ...draft.communicationPreferences,
                      [key]: e.target.checked,
                    },
                  })
                }
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      <Field label="Internal notes" hint="Never shown to the customer." {...field('internalNotes')}>
        <textarea
          rows={4}
          className={inputCls}
          value={draft.internalNotes}
          onChange={(e) => set({ internalNotes: e.target.value })}
        />
      </Field>
    </>
  );
}
